import { Controller, useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import DialogTitle from '@rossum/components/dialog-title/DialogTitle';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@rossum/ui/material';
import ConditionalAuroraChip from '../../../../components/conditional-aurora-chip/ConditionalAuroraChip';
import { AdminAndManagerRestrictor } from '../../../../components/Restrictors';
import { isEmbedded } from '../../../../constants/config';
import { prevalidateAnnotationsEnabledSelector } from '../../../../redux/modules/organizationGroup/selectors';
import {
  automaticSuggestionsEnabledSelector,
  complexLineItemsEnabledSelector,
} from '../../../../redux/modules/ui/selectors';
import { updateUiSettings } from '../../../../redux/modules/user/actions';
import {
  annotationValidationSelector,
  footerLayoutSelector,
  onConfirmNavigateSelector,
  sidebarActionsOrderSelector,
  sidebarAdditionalInfoSelector,
  sortFooterColumnsSelector,
} from '../../../../redux/modules/user/selectors';
import {
  assertMutable,
  sidebarFooterActions,
} from '../sidebar-footer/sidebar-footer-actions/utils';
import { ActionsOrderControl } from './ActionsOrderControl';
import { AdditionalInfoControl } from './AdditionalInfoControl';
import {
  annotationMethodTypes,
  annotationValidationTypes,
  footerLayoutTypes,
  FormValues,
  getOption,
  showSuggestionsTypes,
  sidebarAdditionalInfoTypes,
  sortFooterColumnsTypes,
} from './formValues';
import { LineItemSettingsControl } from './LineItemSettingsControl';
import { NavigationControl } from './NavigationControl';

type LineItemSettingsDialogProps = {
  open: boolean;
  onClose: () => void;
};

export const LineItemSettingsDialog = ({
  open,
  onClose,
}: LineItemSettingsDialogProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const complexLineItemsEnabled = useSelector(complexLineItemsEnabledSelector);
  const automaticSuggestionsEnabled = useSelector(
    automaticSuggestionsEnabledSelector
  );
  const footerLayout = useSelector(footerLayoutSelector);
  const sortFooterColumns = useSelector(sortFooterColumnsSelector);

  const sidebarAdditionalInfo = useSelector(sidebarAdditionalInfoSelector);

  const sidebarOnConfirmNavigation = useSelector(onConfirmNavigateSelector);

  // Only show annotation validation control when prevalidation is enabled for the organization
  // TODO: let's decide if we want to keep this logic here
  const annotationValidation = useSelector(annotationValidationSelector);
  const prevalidateAnnotationsEnabled = useSelector(
    prevalidateAnnotationsEnabledSelector
  );

  const sidebarFooterActionsOrder = useSelector(sidebarActionsOrderSelector);

  const { handleSubmit, control } = useForm<FormValues>({
    defaultValues: {
      actionsOrder:
        sidebarFooterActionsOrder ?? assertMutable(sidebarFooterActions),
      additionalInfo: sidebarAdditionalInfo,
      navigation: sidebarOnConfirmNavigation,
      annotationMethod: complexLineItemsEnabled
        ? 'annotationMethod.suggestions'
        : 'annotationMethod.gridDesign',
      showSuggestions: automaticSuggestionsEnabled
        ? 'showSuggestions.automatically'
        : 'showSuggestions.onDemand',
      annotationValidation:
        annotationValidation === 'on-demand'
          ? 'annotationValidation.onDemand'
          : 'annotationValidation.automatically',
      footerLayout:
        footerLayout === 'table-per-page'
          ? 'footerLayout.table-per-page'
          : 'footerLayout.one-table',
      sortFooterColumns:
        sortFooterColumns === 'automatically'
          ? 'sortColumns.automatically'
          : 'sortColumns.queueSettings',
    },
  });

  const annotationMethodOptions = annotationMethodTypes.map(type => {
    const option = getOption(intl, type);

    // custom title for complex tables
    if (type === 'annotationMethod.suggestions') {
      return {
        ...option,
        chip: <ConditionalAuroraChip />,
      };
    }
    return option;
  });

  const showSuggestionsOptions = showSuggestionsTypes.map(type =>
    getOption(intl, type)
  );

  const annotationValidationOptions = annotationValidationTypes.map(type =>
    getOption(intl, type)
  );

  const sortColumnsOptions = sortFooterColumnsTypes.map(type =>
    getOption(intl, type)
  );

  const footerLayoutOptions = footerLayoutTypes.map(type =>
    getOption(intl, type)
  );

  const additionalInfoOptions = sidebarAdditionalInfoTypes.map(type =>
    getOption(intl, type)
  );

  const onSubmit = (values: FormValues) => {
    const newFooterLayout =
      values.footerLayout === 'footerLayout.table-per-page'
        ? 'table-per-page'
        : 'one-table';
    const newSortFooterColumns =
      values.sortFooterColumns === 'sortColumns.automatically'
        ? 'automatically'
        : 'queue-settings';

    const newComplexLineItems =
      values.annotationMethod === 'annotationMethod.suggestions';

    const newAutomaticSuggestions =
      values.showSuggestions === 'showSuggestions.automatically';

    const newAnnotationValidation =
      values.annotationValidation === 'annotationValidation.onDemand'
        ? 'on-demand'
        : 'automatically';

    dispatch(
      updateUiSettings({
        footerLayout: newFooterLayout,
        sortFooterColumns: newSortFooterColumns,
        annotationValidation: newAnnotationValidation,
        automaticSuggestions: newAutomaticSuggestions,
        sidebarAdditionalInfo: values.additionalInfo,
        sidebarActionsOrder: values.actionsOrder,
        sidebarNavigation: { onConfirm: values.navigation },
        // To prevent reload.
        ...(newComplexLineItems !== complexLineItemsEnabled
          ? { complexLineItems: newComplexLineItems }
          : {}),
      })
    );
    onClose();
  };

  const values = useWatch({
    control,
  });

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ elevation: 0 }}>
      <DialogTitle
        title={intl.formatMessage({
          id: isEmbedded()
            ? 'components.lineItemsSettings.title'
            : 'components.lineItemsSettings.title2',
        })}
        onClose={onClose}
      />
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)} id="line-item-settings">
          <Stack mt={3} spacing={2}>
            {!isEmbedded() ? (
              <>
                <Typography variant="h5">
                  {intl.formatMessage({
                    id: 'components.lineItemsSettings.leftSidebar.title',
                  })}
                </Typography>
                <AdminAndManagerRestrictor>
                  <AdditionalInfoControl
                    control={control}
                    options={additionalInfoOptions}
                  />
                </AdminAndManagerRestrictor>
                <NavigationControl control={control} />
                <ActionsOrderControl control={control} />
                {prevalidateAnnotationsEnabled ? (
                  <Controller
                    control={control}
                    name="annotationValidation"
                    render={({ field }) => (
                      <LineItemSettingsControl
                        name={intl.formatMessage({
                          id: 'components.lineItemsSettings.annotationValidation.title',
                        })}
                        id="annotation-validation"
                        options={annotationValidationOptions}
                        field={field}
                      />
                    )}
                  />
                ) : null}
                <Typography variant="h5">
                  {intl.formatMessage({
                    id: 'components.lineItemsSettings.lineItems.title',
                  })}
                </Typography>
              </>
            ) : null}
            <Stack mt={3} spacing={2}>
              <Controller
                control={control}
                name="annotationMethod"
                render={({ field }) => (
                  <LineItemSettingsControl
                    name={intl.formatMessage({
                      id: 'components.lineItemsSettings.annotationMethod.title',
                    })}
                    id="annotation-method"
                    options={annotationMethodOptions}
                    field={field}
                  />
                )}
              />
              <Controller
                control={control}
                name="showSuggestions"
                render={({ field }) => (
                  <LineItemSettingsControl
                    name={intl.formatMessage({
                      id: 'components.lineItemsSettings.showSuggestions.title',
                    })}
                    id="show-suggestions"
                    options={showSuggestionsOptions}
                    field={field}
                    disabled={
                      values.annotationMethod === 'annotationMethod.gridDesign'
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name="footerLayout"
                render={({ field }) => (
                  <LineItemSettingsControl
                    name={intl.formatMessage({
                      id: 'components.lineItemsSettings.footerLayout.title',
                    })}
                    id="footer-layout"
                    options={footerLayoutOptions}
                    field={field}
                  />
                )}
              />
              <Controller
                control={control}
                name="sortFooterColumns"
                render={({ field }) => (
                  <LineItemSettingsControl
                    name={intl.formatMessage({
                      id: 'components.lineItemsSettings.sortColumns.title',
                    })}
                    id="sort-columns"
                    options={sortColumnsOptions}
                    field={field}
                  />
                )}
              />
            </Stack>
          </Stack>
        </form>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Stack direction="row" spacing={2} justifyContent="end">
          <Button
            type="button"
            variant="outlined"
            color="secondary"
            onClick={onClose}
          >
            {intl.formatMessage({
              id: 'components.lineItemsSettings.close',
            })}
          </Button>
          <Button type="submit" variant="contained" form="line-item-settings">
            {intl.formatMessage({
              id: 'components.lineItemsSettings.save',
            })}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};
